import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Global for HMR / multiple imports in dev
declare global {
  interface Window {
    __supabase?: SupabaseClient;
  }
}

/**
 * Get singleton Supabase client
 * Uses window.__supabase to ensure single instance even with HMR
 */
export function getSupabase(): SupabaseClient {
  // Check if already exists on window (survives HMR)
  if (typeof window !== 'undefined' && window.__supabase) {
    return window.__supabase;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables');
  }

  // Create singleton client
  const client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storageKey: 'ps_auth', // Unique storage key
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'x-client': 'plexisystem-app'
      }
    }
  });

  // Store on window for singleton pattern
  if (typeof window !== 'undefined') {
    window.__supabase = client;
  }

  if (import.meta.env.DEV) {
    console.log('✅ Supabase client initialized (singleton)');
  }

  return client;
}

// Export for backward compatibility
export const supabase = getSupabase();

// Database types
export interface DbClient {
  id?: number;
  nip: string;
  name: string;
  address?: string;
  email?: string;
  phone?: string;
  regon?: string;
  wojewodztwo?: string;
  powiat?: string;
  gmina?: string;
  created_at?: string;
}

export interface DbOffer {
  id?: number;
  offer_number: string;
  share_token: string;
  client_id: number;
  salesperson_id: string;
  salesperson_name: string;
  salesperson_email?: string;
  salesperson_phone?: string;
  project_name?: string;
  status: string;
  total_net: number;
  discount: number;
  discount_value: number;
  total_net_after_discount: number;
  delivery_region: string;
  delivery_cost: number;
  terms_delivery_time: string;
  terms_delivery_method: string;
  terms_payment: string;
  terms_warranty: string;
  terms_validity: string;
  valid_until: string;
  comment?: string;
  internal_notes?: string;
  created_at?: string;
  updated_at?: string;
  version: number;
}

export interface DbOfferItem {
  id?: number;
  offer_id: number;
  position?: number;
  product: string;
  product_name: string;
  expositor_type?: string;
  material: string;
  material_name: string;
  thickness: number;
  width: number;
  height: number;
  depth: number;
  quantity: number;
  unit_price: number;
  total_price: number;
  surface: number;
  weight: number;
  options: any;
  calculations: any;
}
